import { useRef, useState } from 'react'
import { AccountCircleOutlined, CreditCard, EmailOutlined, ImageOutlined, LockOutlined, SwitchAccount, ArrowBack } from '@mui/icons-material'
import { Box, Tab, Tabs, Button, styled } from '@mui/material'
import { getAuth } from 'firebase/auth'
import { useLocation, useNavigate } from 'react-router-dom'
import Require from '../../../../providers/Require'
import logger from '../../../../utils/logger'
import { SettingsLayout } from '../Layout'
import LinkedAccounts from './LinkedAccounts'
import Media from './Media'
import Password from './Password'
import Profile from './Profile'
import Email from './email'
import Membership from './membership/index'
import EmailNotVerified from '../blockers/EmailNotVerified'
import theme from '../../../../config/theme'

interface TabPanelProps {
  children?: React.ReactNode
  index: string
  value: string
}

const StyledTab = styled(Tab)({
  textTransform: 'none',
  '&.MuiButtonBase-root': {
    justifyContent: 'left !important',
    padding: '16px 24px',
    minHeight: 0,
    maxWidth: '100%'
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.customViolet[50],
    border: 'none'
  }
})

function TabPanel({ children = null, value, index }: Readonly<TabPanelProps>) {
  return (
    <Box role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} sx={{ flex: 1 }}>
      {value === index && <Box sx={{ p: { xs: '16px', md: '32px' } }}>{children}</Box>}
    </Box>
  )
}

const MEMBERSHIP = 'membership'
const PROFILE = 'profile'
const PASSWORD = 'password'
const LINKED_ACCOUNTS = 'linked-accounts'
const EMAIL = 'email'
const MEDIA = 'media'

function SettingsIndex() {
  const location = useLocation()
  const navigate = useNavigate()
  const clickCountRef = useRef(1)
  const [tab, setTab] = useState(location.hash.replace('#', '') || PROFILE) // use || not ?? because ?? is falsey for empty string
  const hasIdp = Boolean(getAuth().currentUser?.providerData.find((provider) => ['google.com', 'apple.com'].includes(provider.providerId)))
  const isOnlyIdpUser = hasIdp && getAuth().currentUser?.providerData.length === 1
  logger.info({ hasIdp, isOnlyIdpUser, idps: getAuth().currentUser?.providerData })

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue)
  }

  const handleBack = () => {
    navigate(-clickCountRef.current)
  }

  const setHash = (hash: string) => {
    return () => {
      const redirect = location.hash.replace(tab, hash)
      clickCountRef.current += 1
      navigate(redirect)
    }
  }

  if (!getAuth().currentUser?.emailVerified) {
    return (
      <SettingsLayout>
        <EmailNotVerified />
      </SettingsLayout>
    )
  }

  return (
    <SettingsLayout>
      <Box sx={{ borderBottom: `1px solid ${theme.palette.customGrey[200]}`, padding: '8px 16px' }}>
        <Button onClick={handleBack} size="small" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 0 }}>
          <ArrowBack />
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          margin: { xs: '0', md: '0 16px 0 0' },
          maxWidth: theme.breakpoints.values.xl
        }}
      >
        <Tabs
          value={tab}
          onChange={handleChange}
          aria-label="Settings Tabs"
          allowScrollButtonsMobile
          scrollButtons={false}
          orientation="vertical"
          TabIndicatorProps={{
            style: { display: 'none' }
          }}
          sx={{
            borderRight: { xs: 'none', md: `1px solid ${theme.palette.customGrey[200]}` },
            borderBottom: { xs: `1px solid ${theme.palette.customGrey[200]}`, md: 'none' },
            minHeight: { xs: 'auto', md: '100vh' }
          }}
        >
          <StyledTab icon={<AccountCircleOutlined />} iconPosition="start" label="Profile" value={PROFILE} onClick={setHash(PROFILE)} />
          <StyledTab icon={<CreditCard />} iconPosition="start" label="Membership" value={MEMBERSHIP} onClick={setHash(MEMBERSHIP)} />
          <StyledTab icon={<ImageOutlined />} iconPosition="start" label="Media" value={MEDIA} onClick={setHash(MEDIA)} />
          {!isOnlyIdpUser && <StyledTab icon={<EmailOutlined />} iconPosition="start" label="Email" value={EMAIL} onClick={setHash(EMAIL)} />}
          <StyledTab icon={<LockOutlined />} iconPosition="start" label="Password" value={PASSWORD} onClick={setHash(PASSWORD)} />
          <StyledTab
            icon={<SwitchAccount />}
            iconPosition="start"
            label="Linked Accounts"
            value={LINKED_ACCOUNTS}
            onClick={setHash(LINKED_ACCOUNTS)}
          />
        </Tabs>
        <TabPanel value={tab} index={PROFILE}>
          <Profile />
        </TabPanel>
        <TabPanel value={tab} index={MEMBERSHIP}>
          <Membership />
        </TabPanel>
        <TabPanel value={tab} index={MEDIA}>
          <Media />
        </TabPanel>
        <TabPanel value={tab} index={EMAIL}>
          <Email />
        </TabPanel>
        <TabPanel value={tab} index={PASSWORD}>
          <Password />
        </TabPanel>
        <TabPanel value={tab} index={LINKED_ACCOUNTS}>
          <LinkedAccounts />
        </TabPanel>
      </Box>
    </SettingsLayout>
  )
}

export default function Settings() {
  return (
    <Require validFirebaseSession>
      <SettingsIndex />
    </Require>
  )
}
